import { Controller } from "react-hook-form";
import { WEB_URL } from "@/lib/webUrl";
import { blogPostCreateSchema, type BlogPostCreateInput } from "@ckgo2/shared/schemas";
import { BLOG_CATEGORIES, BLOG_STATUSES } from "@ckgo2/shared/types";
import type { BlogPost, Destination, Trip } from "@ckgo2/shared/types";
import { BLOG_CATEGORY_LABELS as CATEGORY_LABELS } from "@ckgo2/shared/utils";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import MediaPicker from "@/components/common/MediaPicker";
import RichTextEditor from "@/components/editor/RichTextEditor";
import EditPageHeader from "@/components/common/EditPageHeader";
import { useEditPage } from "@/hooks/useEditPage";

export default function BlogPostEditPage() {
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    BlogPostCreateInput,
    BlogPost
  >({
    resourceKey: "blogPost",
    listKey: "blogPosts",
    apiPath: "/api/admin/blog",
    schema: blogPostCreateSchema,
    defaultValues: {
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featuredImage: "",
      featuredImageId: null,
      galleryImages: [],
      galleryImageIds: [],
      category: "travel-tips",
      tags: [],
      destinationId: "",
      relatedTripIds: [],
      authorName: "Banh Mi Travel Team",
      authorAvatar: "",
      authorAvatarId: null,
      readTime: null,
      isFeatured: false,
      status: "draft",
      publishedAt: null,
      sortOrder: 0,
      metaTitle: "",
      metaDescription: "",
    },
    navigateTo: "/blog",
    stayOnSave: true,
    transformOnLoad: (data) => {
      const d = { ...data } as Record<string, unknown>;
      // Convert ISO publishedAt to datetime-local format
      if (d.publishedAt && typeof d.publishedAt === "string") {
        d.publishedAt = (d.publishedAt as string).slice(0, 16);
      }
      if (!d.destinationId) {
        d.destinationId = "";
      }
      return d as unknown as BlogPostCreateInput;
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Fetch destinations for select
  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });

  // Fetch trips for related trips checkboxes
  const { data: tripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  const destinations = destinationsData?.data || [];
  const allTrips = (tripsData?.data || []).filter((t) => t.status === "published");

  const relatedTripIds = watch("relatedTripIds") || [];
  const toggleTripId = (tripId: string) => {
    const next = relatedTripIds.includes(tripId)
      ? relatedTripIds.filter((id) => id !== tripId)
      : [...relatedTripIds, tripId];
    setValue("relatedTripIds", next, { shouldDirty: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/blog"
        isNew={isNew}
        newTitle="New blog post"
        editTitle="Edit blog post"
        entityName={itemData?.title}
        viewUrl={!isNew && watch("slug") ? `${WEB_URL}/blog/${watch("slug")}` : undefined}
        formId="blog-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        isSuccess={saveMutation.isSuccess}
      />

      <form id="blog-form" onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Basic information
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="title" className="label">
                  Title *
                </label>
                <input
                  {...register("title")}
                  type="text"
                  id="title"
                  className={`input ${errors.title ? "input-error" : ""}`}
                  placeholder="e.g. The Ultimate Guide to Vietnamese Street Food"
                />
                {errors.title && (
                  <p className="text-error-600 mt-1 text-sm">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="slug" className="label">
                  URL slug *
                </label>
                <input
                  {...register("slug")}
                  type="text"
                  id="slug"
                  className={`input ${errors.slug ? "input-error" : ""}`}
                  placeholder="e.g. ultimate-vietnamese-street-food-guide"
                />
                {errors.slug && (
                  <p className="text-error-600 mt-1 text-sm">{errors.slug.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="category" className="label">
                  Category *
                </label>
                <select {...register("category")} id="category" className="input">
                  {BLOG_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {CATEGORY_LABELS[cat] || cat}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-error-600 mt-1 text-sm">{errors.category.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="destinationId" className="label">
                  Destination
                </label>
                <select {...register("destinationId")} id="destinationId" className="input">
                  <option value="">No destination</option>
                  {destinations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="excerpt" className="label">
                Excerpt
              </label>
              <textarea
                {...register("excerpt")}
                id="excerpt"
                rows={2}
                className="input"
                placeholder="Brief summary for cards and listings (max 500 chars)..."
                maxLength={500}
              />
            </div>
          </div>

          {/* Content - TipTap */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Content</h2>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Write your blog post content..."
                />
              )}
            />
          </div>

          {/* Gallery Images */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Photo Gallery
            </h2>
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
              <strong>Position:</strong> Gallery appears as a full-width grid section{" "}
              <strong>below the article content</strong> and tags, above related trips. Use the
              Image button in the Content editor toolbar above to insert images{" "}
              <strong>inline within the article text</strong>.
            </div>
            <Controller
              name="galleryImageIds"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="multiple"
                  folder="blog"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Author */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Author</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="authorName" className="label">
                  Author name
                </label>
                <input
                  {...register("authorName")}
                  type="text"
                  id="authorName"
                  className="input"
                  placeholder="e.g. Banh Mi Travel Team"
                />
              </div>
              <div>
                <label htmlFor="readTime" className="label">
                  Read time (minutes)
                </label>
                <input
                  {...register("readTime", { valueAsNumber: true })}
                  type="number"
                  id="readTime"
                  className="input"
                  min={1}
                  placeholder="e.g. 8"
                />
              </div>
              <div>
                <label className="label">Author avatar</label>
                <Controller
                  name="authorAvatarId"
                  control={control}
                  render={({ field }) => (
                    <MediaPicker
                      mode="single"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      folder="blog"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Related Trips */}
          {allTrips.length > 0 && (
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Related trips
              </h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {allTrips.map((trip) => (
                  <label
                    key={trip.id}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={relatedTripIds.includes(trip.id)}
                      onChange={() => toggleTripId(trip.id)}
                      className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{trip.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Tags</h2>
            <textarea
              id="tags"
              rows={2}
              className="input"
              placeholder="street-food, pho, banh-mi, hanoi"
              value={watch("tags")?.join(", ") ?? ""}
              onChange={(e) => {
                const value = e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                setValue("tags", value, { shouldDirty: true });
              }}
            />
            <p className="mt-1 text-xs text-gray-500">Separate with commas</p>
          </div>

          {/* SEO */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">SEO</h2>
            <div className="grid gap-4">
              <div>
                <label htmlFor="metaTitle" className="label">
                  Meta title
                </label>
                <input
                  {...register("metaTitle")}
                  type="text"
                  id="metaTitle"
                  className="input"
                  placeholder="SEO title (max 60 chars)"
                  maxLength={60}
                />
              </div>
              <div>
                <label htmlFor="metaDescription" className="label">
                  Meta description
                </label>
                <textarea
                  {...register("metaDescription")}
                  id="metaDescription"
                  rows={2}
                  className="input"
                  placeholder="SEO description (max 160 chars)"
                  maxLength={160}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Featured image
            </h2>
            <Controller
              name="featuredImageId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="single"
                  folder="blog"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="status" className="label">
                  Status
                </label>
                <select {...register("status")} id="status" className="input">
                  {BLOG_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="publishedAt" className="label">
                  Published at
                </label>
                <input
                  {...register("publishedAt")}
                  type="datetime-local"
                  id="publishedAt"
                  className="input"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Auto-set when status changes to Published
                </p>
              </div>

              <div>
                <label htmlFor="sortOrder" className="label">
                  Sort order
                </label>
                <input
                  {...register("sortOrder", { valueAsNumber: true })}
                  type="number"
                  id="sortOrder"
                  className="input"
                  min={0}
                />
                <p className="mt-1 text-xs text-gray-500">Lower = higher priority</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  {...register("isFeatured")}
                  type="checkbox"
                  id="isFeatured"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isFeatured"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Featured post
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
